import type { MarkSpec } from "prosemirror-model";

import { domAttrs, stringAttr } from "./attrs.js";

/** Mark names follow `docs/spec/markdown-dialect.md` §5. */
export const MarkName = {
  link: "link",
  strong: "strong",
  em: "em",
  underline: "underline",
  strikethrough: "strikethrough",
  codeInline: "code_inline",
  highlight: "highlight",
  placeholder: "placeholder",
  comment: "comment",
} as const;

export type MarkNameValue = (typeof MarkName)[keyof typeof MarkName];

/**
 * Every mark except `link` and `comment` is mixable: the serializer may reorder
 * it so a run that is already open stays open, and whitespace at the edges of
 * a run moves outside the delimiters.
 */
export const MIXABLE_MARKS: readonly string[] = [
  MarkName.strong,
  MarkName.em,
  MarkName.underline,
  MarkName.strikethrough,
  MarkName.codeInline,
  MarkName.highlight,
  MarkName.placeholder,
];

/** The six highlight swatches the editor offers. Colour is lost in markdown. */
export const HIGHLIGHT_COLORS = [
  "#FDEA9B",
  "#FED46A",
  "#FA551E",
  "#B4DC19",
  "#C8AFF0",
  "#3CBEFC",
] as const;

function isLineThrough(value: string): boolean {
  return /line-through/i.test(value);
}

export const marks: Record<MarkNameValue, MarkSpec> = {
  // `link` is declared first so it ranks outermost and a link spanning
  // differently formatted runs stays one link.
  link: {
    attrs: { href: { default: "" }, title: { default: null } },
    inclusive: false,
    parseDOM: [
      {
        tag: "a[href]",
        getAttrs: (dom) => ({
          href: dom.getAttribute("href") ?? "",
          title: dom.getAttribute("title"),
        }),
      },
    ],
    toDOM: (mark) => [
      "a",
      domAttrs({ href: stringAttr(mark, "href") ?? "", title: stringAttr(mark, "title") }),
      0,
    ],
  },

  strong: {
    parseDOM: [
      { tag: "strong" },
      { tag: "b", getAttrs: (dom) => (dom.style.fontWeight === "normal" ? false : null) },
      {
        style: "font-weight",
        getAttrs: (value) => (/^(bold(er)?|[5-9]\d{2,})$/.test(value) ? null : false),
      },
    ],
    toDOM: () => ["strong", 0],
  },

  em: {
    parseDOM: [
      { tag: "em" },
      { tag: "i", getAttrs: (dom) => (dom.style.fontStyle === "normal" ? false : null) },
      { style: "font-style=italic" },
    ],
    toDOM: () => ["em", 0],
  },

  underline: {
    parseDOM: [
      { tag: "u" },
      { style: "text-decoration", getAttrs: (value) => (/underline/i.test(value) ? null : false) },
      {
        style: "text-decoration-line",
        getAttrs: (value) => (/underline/i.test(value) ? null : false),
      },
    ],
    toDOM: () => ["u", 0],
  },

  strikethrough: {
    parseDOM: [
      { tag: "s" },
      { tag: "del" },
      { tag: "strike" },
      { style: "text-decoration", getAttrs: (value) => (isLineThrough(value) ? null : false) },
      { style: "text-decoration-line", getAttrs: (value) => (isLineThrough(value) ? null : false) },
    ],
    toDOM: () => ["s", 0],
  },

  code_inline: {
    code: true,
    // §5: an inline code span cannot also be a placeholder or a highlight.
    // `mention` is listed there too, but it is a node in this schema, and
    // naming a non-mark in `excludes` makes schema construction throw.
    excludes: "placeholder highlight",
    parseDOM: [{ tag: "code", getAttrs: (dom) => (dom.closest("pre") ? false : null) }],
    toDOM: () => ["code", 0],
  },

  highlight: {
    attrs: { color: { default: null } },
    parseDOM: [
      { tag: "mark", getAttrs: (dom) => ({ color: dom.getAttribute("data-color") }) },
      {
        tag: "span[data-highlight]",
        getAttrs: (dom) => ({ color: dom.getAttribute("data-highlight") }),
      },
      {
        // Clipboard HTML from other editors marks highlights with a background.
        tag: "span",
        getAttrs: (dom) => {
          const background = dom.style.backgroundColor;
          if (!background || background === "transparent") return false;
          return { color: background };
        },
      },
    ],
    toDOM: (mark) => ["mark", domAttrs({ "data-color": stringAttr(mark, "color") }), 0],
  },

  placeholder: {
    parseDOM: [{ tag: "span[data-placeholder]" }],
    toDOM: () => ["span", { "data-placeholder": "true" }, 0],
  },

  comment: {
    attrs: {
      id: { default: "" },
      userId: { default: "" },
      resolved: { default: false },
      draft: { default: false },
    },
    // Typing at either edge must not extend an anchor, and an empty `excludes`
    // lets several comment anchors overlap the same text.
    inclusive: false,
    excludes: "",
    parseDOM: [
      {
        tag: "span[data-comment-id]",
        getAttrs: (dom) => ({
          id: dom.getAttribute("data-comment-id") ?? "",
          userId: dom.getAttribute("data-user-id") ?? "",
          resolved: dom.getAttribute("data-resolved") === "true",
          draft: dom.getAttribute("data-draft") === "true",
        }),
      },
    ],
    toDOM: (mark) => [
      "span",
      domAttrs({
        id: `comment-${stringAttr(mark, "id") ?? ""}`,
        "data-comment-id": stringAttr(mark, "id"),
        "data-user-id": stringAttr(mark, "userId"),
        "data-resolved": mark.attrs.resolved === true ? "true" : "false",
        "data-draft": mark.attrs.draft === true ? "true" : "false",
      }),
      0,
    ],
  },
};
